import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ArrowUpRight, CreditCard, FileText, Landmark } from "lucide-react";
import { Badge, Button, DetailSectionTitle, EmptyState } from "@/components/ui";
import { SectionCard } from "@/components/common";
import { formatFullDate } from "@/components/tickets/config";
import type { AdminBookingDetailResponse } from "@/services/bookingService";
import { toastApiError } from "@/lib/errorMessages";
import { formatMoney } from "@/lib/money";
import { openStorageUrlInNewTab } from "@/lib/storageFetch";
import {
  economicEventStatusLabel,
  economicEventTypeLabel,
} from "@/lib/economicEventLabels";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { PartnerReceiptUploadStatus, Permission } from "@/types";
import { ReceiptDisplay } from "./ReceiptDisplay";
import type { BookingListRow } from "./bookingViewModel";

interface BookingAdminPaymentSectionProps {
  row: BookingListRow;
  detail: AdminBookingDetailResponse;
  canAdjustReceiptTotal: boolean;
  isAdjustingReceipt: boolean;
  onAdjustReceiptClick: () => void;
}

const linkClass =
  "inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-900";

function uploadDotClass(status: string): string {
  if (status === PartnerReceiptUploadStatus.CONFIRMED) return "bg-success-500";
  if (status === "ocr_failed" || status === "partner_rejected") {
    return "bg-error-500";
  }
  if (status === "ocr_succeeded") return "bg-primary-500";
  return "bg-surface-300";
}

/** Admin booking detail → "Payment" tab: payment record or receipt, upload audit, economic events. */
export function BookingAdminPaymentSection({
  row,
  detail,
  canAdjustReceiptTotal,
  isAdjustingReceipt,
  onAdjustReceiptClick,
}: BookingAdminPaymentSectionProps) {
  const { t } = useTranslation("bookings");
  const { t: tPayments } = useTranslation("payments");
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canListExternalPayments = hasPermission(Permission.EXTERNAL_PAYMENTS_LIST);
  const canListEconomicEvents = hasPermission(Permission.ECONOMIC_EVENTS_LIST);

  const booking = detail.booking;
  const payment = canListExternalPayments ? detail.external_payment : null;
  const economicEvents = canListEconomicEvents ? detail.economic_events : [];
  const receiptUploads = booking.partner_receipt_upload_history ?? [];
  const amountSource = booking.partner_receipt_amount_source ?? null;
  const isReceiptBased = row.isReceiptBased;

  const confirmedManualUpload =
    amountSource === "MANUAL"
      ? receiptUploads.find(
          (u) => u.status === PartnerReceiptUploadStatus.CONFIRMED,
        )
      : undefined;
  const manualRegistrationAt =
    confirmedManualUpload?.confirmed_at ??
    confirmedManualUpload?.uploaded_at ??
    null;

  const inlineEmpty = (title: string) => (
    <EmptyState
      size="sm"
      showIcon={false}
      title={title}
      className="items-start px-0 py-0 text-left"
    />
  );

  const receiptBlock = (
    <>
      {row.receiptTotalCents != null ? (
        <div className="space-y-2">
          <ReceiptDisplay
            receiptTotalCents={row.receiptTotalCents}
            receiptUrl={row.receiptUrl}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            {amountSource ? (
              <p className="text-xs text-surface-500">
                {t(`adminDetail.receipt.amountSource.${amountSource}`)}
              </p>
            ) : (
              <span />
            )}
            {canAdjustReceiptTotal ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onAdjustReceiptClick}
                disabled={isAdjustingReceipt}
              >
                {t("adminReceiptAdjust.action")}
              </Button>
            ) : null}
          </div>
        </div>
      ) : booking.partner_receipt_manual_amount_required ? (
        <p className="text-sm text-warning-800">
          {t("adminDetail.receipt.manualRequired")}
        </p>
      ) : (
        inlineEmpty(t("adminDetail.receipt.noneYet"))
      )}

      <div className="space-y-2 border-t border-surface-200/70 pt-3.5">
        <DetailSectionTitle>{t("adminDetail.receipt.auditTitle")}</DetailSectionTitle>
        {receiptUploads.length === 0 && !manualRegistrationAt ? (
          inlineEmpty(t("adminDetail.receipt.auditEmpty"))
        ) : (
          <ol className="space-y-0">
            {receiptUploads.map((upload, index) => {
              const eventAt =
                upload.confirmed_at ?? upload.validated_at ?? upload.uploaded_at;
              const amountCents =
                upload.confirmed_total_cents ?? upload.ocr_total_cents;
              const hasNext =
                index < receiptUploads.length - 1 || amountSource === "MANUAL";
              return (
                <li
                  key={`${upload.storage_path}-${index}`}
                  className="relative flex gap-3"
                >
                  <div className="flex w-3 shrink-0 flex-col items-center">
                    <span
                      className={cn(
                        "mt-1.5 h-2 w-2 rounded-full",
                        uploadDotClass(upload.status),
                      )}
                    />
                    {hasNext ? (
                      <span className="mt-1 w-px flex-1 bg-surface-200" />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1 pb-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                      <p className="text-sm font-medium text-surface-900">
                        {t(`adminDetail.receipt.uploadStatus.${upload.status}`, {
                          defaultValue: upload.status,
                        })}
                      </p>
                      {amountCents != null ? (
                        <p className="text-sm font-semibold tabular-nums text-surface-900">
                          {formatMoney(amountCents)}
                        </p>
                      ) : null}
                    </div>
                    {eventAt ? (
                      <p className="mt-0.5 text-xs text-surface-500">
                        {formatFullDate(eventAt)}
                      </p>
                    ) : null}
                    {upload.file_url ? (
                      <button
                        type="button"
                        className="mt-1 text-xs font-medium text-primary-700 hover:text-primary-900"
                        onClick={() => {
                          void openStorageUrlInNewTab(upload.file_url).catch(
                            (err) =>
                              toastApiError(
                                err,
                                "generic",
                                t("adminDetail.receipt.viewUploadError"),
                              ),
                          );
                        }}
                      >
                        {t("adminDetail.receipt.viewUpload")}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
            {amountSource === "MANUAL" ? (
              <li className="relative flex gap-3">
                <div className="flex w-3 shrink-0 flex-col items-center">
                  <span className="mt-1.5 h-2 w-2 rounded-full bg-warning-500" />
                </div>
                <div className="min-w-0 flex-1 pb-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <p className="text-sm font-medium text-surface-900">
                      {t("adminDetail.receipt.manualRegistered")}
                    </p>
                    {row.receiptTotalCents != null ? (
                      <p className="text-sm font-semibold tabular-nums text-surface-900">
                        {formatMoney(row.receiptTotalCents)}
                      </p>
                    ) : null}
                  </div>
                  {manualRegistrationAt ? (
                    <p className="mt-0.5 text-xs text-surface-500">
                      {formatFullDate(manualRegistrationAt)}
                    </p>
                  ) : null}
                </div>
              </li>
            ) : null}
          </ol>
        )}
      </div>
    </>
  );

  const paymentBlock = !canListExternalPayments ? (
    <p className="text-sm text-surface-500">{t("adminDetail.payment.noAccess")}</p>
  ) : !payment ? (
    inlineEmpty(t("adminDetail.payment.none"))
  ) : (
    <div className="flex flex-col gap-3 rounded-lg border border-surface-200 bg-surface-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between dark:bg-surface-100/40">
      <div className="min-w-0">
        <p className="text-xs font-medium text-surface-500">
          {t("adminDetail.payment.amount")}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2">
          <p className="text-[15px] font-semibold tabular-nums text-surface-900">
            {formatMoney(payment.amount_cents, payment.currency)}
          </p>
          <Badge variant="default" className="font-normal">
            {payment.status}
          </Badge>
        </div>
      </div>
      <Link
        to={`/admin/payments/external-payments?paymentId=${payment.id}`}
        className={linkClass}
      >
        {t("adminDetail.payment.viewPayment")}
        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
      </Link>
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionCard
        title={
          isReceiptBased
            ? t("adminDetail.sections.receipt")
            : t("adminDetail.sections.payment")
        }
        icon={isReceiptBased ? FileText : CreditCard}
        badge={
          <Badge variant="default" className="font-normal">
            {t(`adminDetail.payment.models.${booking.activity_payment_model}`)}
          </Badge>
        }
      >
        {isReceiptBased ? receiptBlock : paymentBlock}
      </SectionCard>

      <SectionCard
        title={t("adminDetail.economicEvents.title")}
        icon={Landmark}
        count={canListEconomicEvents ? economicEvents.length : undefined}
      >
        {!canListEconomicEvents ? (
          <p className="text-sm text-surface-500">
            {t("adminDetail.economicEvents.noAccess")}
          </p>
        ) : economicEvents.length === 0 ? (
          inlineEmpty(t("adminDetail.economicEvents.none"))
        ) : (
          <ul className="divide-y divide-surface-200/70 overflow-hidden rounded-lg border border-surface-200">
            {economicEvents.map((event) => (
              <li key={event.id}>
                <Link
                  to={`/admin/payments/events/${event.id}`}
                  className="group flex items-center justify-between gap-3 px-3.5 py-2.5 transition-colors hover:bg-surface-50 dark:hover:bg-surface-100/50"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-surface-900">
                        {economicEventTypeLabel(event.type, tPayments)}
                      </p>
                      <Badge variant="default" className="font-normal">
                        {economicEventStatusLabel(event.status, tPayments)}
                      </Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-primary-700 group-hover:text-primary-900">
                      {t("adminDetail.economicEvents.viewEvent")}
                    </p>
                  </div>
                  <p className="shrink-0 text-sm font-semibold tabular-nums text-surface-900">
                    {formatMoney(event.gross_amount_cents, event.currency)}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
